import { useEffect, useRef, useState, type ReactNode } from "react";

import clsx from "clsx";
import { useToasts } from "react-toast-notifications";

import { GenericTooltip } from "@components/tooltip-container";
import { type SavedSearchTarget } from "@features/search-input/types";
import {
  safePolygon,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { DELETE_SAVED_SEARCH, FETCH_SAVED_SEARCHES } from "@gql/saved-searches";
import { useNorthbeamMutation } from "@hooks/http";
import { useFuzzySearch } from "@hooks/use-fuzzy-search";
import {
  SavedSearchMethod,
  type DeleteSavedSearch,
  type DeleteSavedSearchVariables,
  type FetchSavedSearches_me_savedSearches,
} from "@north-beam/nb-client-side";
import Spinner from "@shared/spinner";
import { useVirtualizer } from "@tanstack/react-virtual";

interface Props {
  target: SavedSearchTarget;
  savedSearches: FetchSavedSearches_me_savedSearches[];
  onSelectSavedSearch(value: FetchSavedSearches_me_savedSearches): void;
  onDeleteSavedSearch(value: FetchSavedSearches_me_savedSearches): void;
}

export const SavedSearchesSubmenu = ({
  target,
  savedSearches,
  onSelectSavedSearch,
  onDeleteSavedSearch,
}: Props) => {
  const scrollElRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    transform: false,
    placement: "right-start",
  });

  const hover = useHover(context, {
    delay: { close: 1000 },
    handleClose: safePolygon({
      requireIntent: false,
      blockPointerEvents: true,
      buffer: 1,
    }),
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const [filteredOptions, { clearSearch }] = useFuzzySearch(
    savedSearches as (FetchSavedSearches_me_savedSearches &
      Record<string, unknown>)[],
    {
      searchKeys: ["name"],
    },
  );

  const rowVirtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollElRef.current,
    estimateSize: () => 36,
    overscan: 5,
  });

  useEffect(() => {
    if (isOpen) {
      clearSearch();
    }
  }, [isOpen, clearSearch]);

  return (
    <div>
      <button
        className="block w-full text-left py-1.5 px-4 hover:bg-cosmic-4 truncate"
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <div className="flex items-center">
          <i
            className={clsx(
              "fa-regular fa-sharp fa-ellipsis text-[0.8rem]",
              "text-primary mt-0.5 mr-2 w-4",
            )}
          />
          <span className="flex-1">Saved Searches</span>
          <i className="fa-regular fa-sharp fa-chevron-right text-[0.7rem] text-primary" />
        </div>
      </button>
      {isOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className={clsx(
            "absolute z-10 pt-2 pb-2 bg-white rounded-lg elevation-1 text-sm w-[300px]",
          )}
          {...getFloatingProps()}
          onMouseDown={(e) => e.preventDefault()}
        >
          {filteredOptions.length === 0 && (
            <div className="px-4 py-2 text-disabled">NO RESULTS FOUND</div>
          )}
          <div ref={scrollElRef} className="max-h-[300px] overflow-y-auto">
            <div
              style={{
                position: "relative",
                height: `${rowVirtualizer.getTotalSize()}px`,
              }}
            >
              {rowVirtualizer.getVirtualItems().map((e) => {
                const row = filteredOptions[e.index];

                return (
                  <DeletableOption
                    key={row.id}
                    target={target}
                    start={e.start}
                    id={row.id}
                    onClick={() => onSelectSavedSearch(row)}
                    onDelete={() => onDeleteSavedSearch(row)}
                  >
                    {row.searchMethod === SavedSearchMethod.regex && (
                      <i
                        className={clsx(
                          "fa-regular fa-sharp fa-function text-[0.8rem]",
                          "text-primary mr-2 w-4",
                        )}
                      />
                    )}
                    {row.searchMethod === SavedSearchMethod.comma && (
                      <i
                        className={clsx(
                          "fa-regular fa-sharp fa-list text-[0.8rem] text-primary",
                          "mr-2 w-4",
                        )}
                      />
                    )}
                    {row.name}
                  </DeletableOption>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DeletableOption = ({
  target,
  start,
  id,
  children,
  onClick,
  onDelete,
}: {
  target: SavedSearchTarget;
  start: number;
  id: string;
  children: ReactNode;
  onClick?(): void;
  onDelete?(): void;
}) => {
  const [isDeleteButtonVisible, setDeleteButtonVisible] = useState(false);
  const [aboutToDelete, setAboutToDelete] = useState(false);

  const { addToast } = useToasts();

  const [deleteSavedSearch, { loading }] = useNorthbeamMutation<
    DeleteSavedSearch,
    DeleteSavedSearchVariables
  >(DELETE_SAVED_SEARCH, {
    refetchQueries: [{ query: FETCH_SAVED_SEARCHES, variables: { target } }],
    awaitRefetchQueries: true,
  });

  return (
    <div
      className={clsx("h-9 flex items-center absolute top-0 left-0 w-full", {
        "hover:bg-cosmic-4": !aboutToDelete,
        "bg-mars-2": aboutToDelete,
      })}
      style={{ transform: `translateY(${start}px)` }}
      onClick={onClick}
      onMouseEnter={() => setDeleteButtonVisible(true)}
      onMouseLeave={() => setDeleteButtonVisible(false)}
    >
      {aboutToDelete ? (
        <div className="text-xs px-4">
          <button
            className={clsx(
              "text-mars font-semibold mr-3 hover:bg-white px-3 py-1 rounded",
              "inline-flex items-center justify-center w-[100px] align-bottom",
            )}
            disabled={loading}
            onClick={async (e) => {
              e.stopPropagation();

              await deleteSavedSearch({ variables: { id } });

              addToast("Search has been deleted", {
                appearance: "warning",
                autoDismiss: true,
              });

              onDelete && onDelete();
            }}
          >
            {loading ? <Spinner colorClassname="text-mars" /> : "YES, DELETE"}
          </button>
          {!loading && (
            <button
              className="text-cosmic+1 font-semibold hover:bg-white px-3 py-1 rounded"
              onClick={(e) => {
                e.stopPropagation();
                setAboutToDelete(false);
              }}
            >
              CANCEL
            </button>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-between w-full cursor-pointer px-4">
          <span className="truncate">{children}</span>
          <GenericTooltip
            noInfoCircle
            content="Delete"
            mouseEnterDelay={0.5}
            overlayClassName="pointer-events-none"
          >
            <button
              className={clsx(
                "w-6 h-6 rounded-full text-mars+2 ml-2 hover:bg-white",
                { invisible: !isDeleteButtonVisible },
              )}
              onClick={(e) => {
                e.stopPropagation();
                setAboutToDelete(true);
              }}
            >
              <i className="fa-regular fa-sharp fa-trash text-xs" />
            </button>
          </GenericTooltip>
        </div>
      )}
    </div>
  );
};
